/**
 * Industrial M2M cellular routers — Digi + Robustel + MOXA.
 *
 * Three brands that own the industrial M2M / SCADA / oil-and-gas
 * cellular market. Common pattern: DIN-rail metal housing, dual SIM
 * for carrier failover, RS-232/485 serial, digital I/O, wide-input DC
 * power for solar / battery deployments, -40..+70 °C operating
 * range.
 *
 * Compiled: 2026-05-15, release/v1.6.7-23k-catalog.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const INDUSTRIAL_CELLULAR_ANTENNAS: EquipmentCatalogEntry[] = [
  // ═══════════════════════════════════════════════════════════════════════
  // Digi International — Digi TransPort WR54
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.digikey.com/en/product-highlight/d/digi-intl/wr54-digi-transport-dual-sim-cellular-routers
  // SRC: https://media.digikey.com/pdf/Data%20Sheets/Digi%20International%20PDFs/DIGI_WR54_Preliminary_11-30-18.pdf
  // SRC: https://www.amazon.com/WR54-A206-Digi-Worldwide-Cellular-Ethernet/dp/B07PYMXSJR
  //      Digi TransPort WR54: dual concurrent LTE Cat 11 modules
  //      (600 Mbps each) with link failover + load balance, 4× GbE
  //      Ethernet (each port configurable as isolated LAN OR all
  //      four as a Layer-2 switch), 1× DB-9 RS-232, Wi-Fi 5
  //      802.11ac (867 Mbps). Digi TrustFence security suite with
  //      cryptographic co-processor for sensitive-data protection.
  //      Dual-core MIPS 880 MHz CPU, PCIe 2.0 internal bus.
  {
    id: 'digi-wr54',
    manufacturer: 'Digi',
    model: 'TransPort WR54 (Dual LTE)',
    category: 'cellular-antenna',
    tagline:
      'TransPort WR54 — dual concurrent Cat 11, 4× GbE, RS-232, Wi-Fi 5, TrustFence',
    verified: true,
    datasheetUrl: 'https://media.digikey.com/pdf/Data%20Sheets/Digi%20International%20PDFs/DIGI_WR54_Preliminary_11-30-18.pdf',
    specs: {
      technology: 'lte-cat12', // schema doesn't include cat11 — cat12 is closest higher tier
      mimo: '2×2',
      pattern: 'omni',
      gainDbi: 2,
      simSlots: 2,
      ipRating: 'IP30',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // Robustel — R2000-4L
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://eshop.sectron.eu/en/robustel-lte-router-r2000-4l-fw-2-0-11-demo/p-12831/
  // SRC: https://www.robustel.com/product_cat/cellular-router/
  // SRC: https://idatanet.com.au/product/r2000-4l/
  //      Robustel R2000-4L: rugged 2G/3G/4G cellular router on RobustOS
  //      Linux, dual SIM redundancy, 2× LAN + 1× WAN Ethernet, 2×
  //      serial (configurable RS-232 4-wire or RS-485 half-duplex),
  //      2× DI + 1× DO digital, 9-26 VDC wide-input (some variants
  //      9-36 VDC), IPSec / OpenVPN / GRE VPN tunnels, web/SMS/CLI
  //      management, metal enclosure with DIN-rail / wall / desktop
  //      mounting options.
  {
    id: 'robustel-r2000-4l',
    manufacturer: 'Robustel',
    model: 'R2000-4L',
    category: 'cellular-antenna',
    tagline:
      'R2000-4L — Cat 4 industrial, dual SIM, 3× Ethernet, RS-232/485, DI/DO, RobustOS',
    verified: true,
    datasheetUrl: 'https://www.robustel.com/product_cat/cellular-router/',
    specs: {
      technology: 'lte-cat4',
      mimo: '2×2',
      pattern: 'omni',
      gainDbi: 2,
      simSlots: 2,
      ipRating: 'IP30',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // MOXA — OnCell G3470A-LTE
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.moxa.com/en/products/industrial-network-infrastructure/cellular-gateways-routers/cellular-gateways/oncell-g3470a-lte-series
  // SRC: https://www.moxa.com/Moxa/media/PDIM/S100000297/moxa-oncell-g3470a-lte-series-datasheet-v1.0.pdf
  // SRC: https://www.amazon.com/OnCell-G3470A-LTE-US-T-Industrial-Ethernet-Gateway/dp/B07N8DW581
  //      MOXA OnCell G3470A-LTE: industrial LTE gateway with integrated
  //      4-port Gigabit Ethernet switch (10/100/1000BaseT), dual SIM
  //      for carrier failover, dual power input redundancy, 1× SMA
  //      female GPS antenna + 2× SMA female cellular antennas. Power
  //      + antenna isolation design for harsh industrial environments
  //      (variable frequency drives, motor noise, lightning). DIN rail
  //      mountable (66.3 mm width). Standard model 30-55 °C, -T variant
  //      extends to -30..+70 °C.
  {
    id: 'moxa-oncell-g3470a-lte',
    manufacturer: 'MOXA',
    model: 'OnCell G3470A-LTE',
    category: 'cellular-antenna',
    tagline:
      'OnCell G3470A — industrial LTE + 4-port GbE switch, dual SIM, GPS, isolation design',
    verified: true,
    datasheetUrl:
      'https://www.moxa.com/en/products/industrial-network-infrastructure/cellular-gateways-routers/cellular-gateways/oncell-g3470a-lte-series',
    specs: {
      technology: 'lte-cat4',
      mimo: '2×2',
      pattern: 'omni',
      gainDbi: 2,
      simSlots: 2,
      ipRating: 'IP30',
    },
  },
];
